import { CSSProperties, useEffect, useRef, useState } from "react";
import React from "react";

import { ChevronDown } from "lucide-react";
import { createPortal } from "react-dom";

import { Button } from "@/components/Button/Button";
import type { ButtonProps } from "@/components/Button/Button";
import { cn } from "@/lib/utils";

export type SelectButtonItem = {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  onSelect: () => void;
};

type SelectButtonProps = Omit<
  ButtonProps,
  "onClick" | "children" | "icon" | "iconPosition"
> & {
  label: string;
  items: SelectButtonItem[];
};

export function SelectButton({
  label,
  items,
  variant = "outline",
  size = "sm",
  disabled,
  className,
  ...rest
}: SelectButtonProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
      zIndex: 9999,
    });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant={variant}
        size={size}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        icon={
          <ChevronDown
            size={13}
            className={cn(
              "opacity-60 transition-transform duration-150",
              open && "rotate-180"
            )}
          />
        }
        iconPosition="end"
        className={className}
        {...rest}
      >
        {label}
      </Button>

      {open &&
        createPortal(
          <ul
            style={menuStyle}
            className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl text-sm overflow-hidden"
          >
            {items.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  disabled={item.disabled}
                  onMouseDown={() => {
                    if (item.disabled) return;
                    item.onSelect();
                    setOpen(false);
                  }}
                  className={cn(
                    "w-full text-left px-3 transition-colors flex items-start gap-2.5",
                    item.description
                      ? "py-2.5"
                      : "py-2 items-center whitespace-nowrap",
                    item.disabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-blue-50 dark:hover:bg-blue-900/30"
                  )}
                >
                  {item.icon && (
                    <span
                      className={cn(
                        "text-muted-foreground shrink-0",
                        item.description && "mt-0.5"
                      )}
                    >
                      {item.icon}
                    </span>
                  )}
                  <span className="flex flex-col min-w-0">
                    <span className="leading-snug">{item.label}</span>
                    {item.description && (
                      <span className="text-[11px] text-muted-foreground leading-snug mt-0.5 whitespace-normal">
                        {item.description}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
}
